import React, { useState, useEffect, useRef } from 'react';
import {
  userService,
  formatApiError,
  PIN_LENGTH,
  pinDigits,
  emptyPinArray,
  rememberPinLength,
  validateNewPin,
} from '../../../../services/api';
import { PinInput, PinLengthToggle } from '../../../../components/ui';
import AuthLogo from '../AuthLogo';
import styles from '../login/loginForm.module.css';
import localStyles from './forgotPinForm.module.css';

const RESET_CODE_LENGTH = 8;
const emptyCode = () => Array(RESET_CODE_LENGTH).fill('');

/**
 * Flujo "Olvidé mi PIN" (como la app): email → código 8 chars → nuevo PIN.
 */
export default function ForgotPinForm({ onBack, onSuccess }) {
  const [step, setStep] = useState(1); // 1 email, 2 code, 3 new pin
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(emptyCode);
  // Nuevo PIN de 4 o 6 dígitos (6 por defecto: más seguro)
  const [pinLength, setPinLength] = useState(PIN_LENGTH);
  const [newPin, setNewPin] = useState(() => emptyPinArray(PIN_LENGTH));
  const [confirmPin, setConfirmPin] = useState(() => emptyPinArray(PIN_LENGTH));
  const newPinRef = useRef(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const verifyingRef = useRef(false);

  const isValidEmail = (value) =>
    Boolean(value.trim()) && value.includes('@') && value.includes('.');

  const handleSendCode = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');
    const trimmed = email.trim().toLowerCase();
    if (!isValidEmail(trimmed)) {
      setError('Ingresa un correo válido');
      return;
    }
    setLoading(true);
    try {
      const result = await userService.requestResetCode(trimmed);
      if (!result.ok) {
        setError(formatApiError(result.data) || 'Error al enviar código');
        return;
      }
      setEmail(trimmed);
      setCode(emptyCode());
      setStep(2);
      setSuccess('Te enviamos un código a tu correo');
    } catch {
      setError('Error de conexión al enviar el código');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (codeArray) => {
    if (verifyingRef.current || loading) return;
    const codeStr = (codeArray || code).join('');
    if (codeStr.length < RESET_CODE_LENGTH) {
      setError('Código incompleto');
      return;
    }
    verifyingRef.current = true;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const result = await userService.verifyResetCode(email, codeStr);
      if (!result.ok) {
        setError(formatApiError(result.data) || 'Código inválido o expirado');
        setCode(emptyCode());
        return;
      }
      setStep(3);
      setSuccess('');
    } catch {
      setError('Error de conexión al verificar el código');
    } finally {
      setLoading(false);
      verifyingRef.current = false;
    }
  };

  useEffect(() => {
    if (step === 2 && code.every((c) => c !== '')) {
      handleVerifyCode(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, step]);

  // Cambiar entre PIN de 4 y 6 dígitos: limpia ambos campos y enfoca el nuevo PIN
  const handlePinLengthChange = (length) => {
    setPinLength(length);
    setNewPin(emptyPinArray(length));
    setConfirmPin(emptyPinArray(length));
    setError('');
    newPinRef.current?.focus();
  };

  const handleResetPin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const pin = pinDigits(newPin);
    const confirm = pinDigits(confirmPin);
    const pinError = validateNewPin(pin, pinLength);
    if (pinError) {
      setError(pinError);
      return;
    }
    if (pin !== confirm) {
      setError('Los PIN no coinciden');
      return;
    }
    setLoading(true);
    try {
      const result = await userService.resetPassword(
        email,
        code.join(''),
        pin,
        confirm
      );
      if (!result.ok) {
        setError(formatApiError(result.data) || 'Error al cambiar el PIN');
        return;
      }
      rememberPinLength(pinLength);
      setSuccess('Cambio de clave exitoso. Ya puedes iniciar sesión.');
      window.setTimeout(() => {
        onSuccess?.();
      }, 1200);
    } catch {
      setError('Error de conexión al cambiar el PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setCode(emptyCode());
    await handleSendCode();
  };

  return (
    <div className={styles.card}>
      <AuthLogo className={styles.logo} imgClassName={styles.logoImg} />

      {step === 1 && (
        <form onSubmit={handleSendCode}>
          <button type="button" className={styles.backBtn} onClick={onBack}>
            ← Volver
          </button>
          <h2 className={styles.title}>Olvidé mi PIN</h2>
          <p className={styles.subtitle}>
            Ingresa el correo de tu cuenta y te enviaremos un código de
            verificación.
          </p>
          <label className={styles.label} htmlFor="forgot-email">
            Correo electrónico
          </label>
          <input
            id="forgot-email"
            type="email"
            className={localStyles.emailInput}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ejemplo@mail.com"
            autoComplete="email"
            disabled={loading}
          />
          {error && <p className={styles.error}>{error}</p>}
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={loading}
          >
            {loading ? 'Enviando...' : 'Enviar'}
          </button>
        </form>
      )}

      {step === 2 && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleVerifyCode();
          }}
        >
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => {
              setStep(1);
              setError('');
              setSuccess('');
              setCode(emptyCode());
            }}
          >
            ← Volver
          </button>
          <h2 className={styles.title}>Verificar código</h2>
          <p className={styles.subtitle}>
            Ingresa el código de {RESET_CODE_LENGTH} caracteres enviado a
            <br />
            <strong>{email}</strong>
          </p>
          <div className={styles.pinWrapper}>
            <PinInput
              length={RESET_CODE_LENGTH}
              value={code}
              onChange={setCode}
              disabled={loading}
              masked={false}
              showToggle={false}
              mode="alphanumeric"
            />
          </div>
          {success && <p className={localStyles.success}>{success}</p>}
          {error && <p className={styles.error}>{error}</p>}
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={loading || code.some((c) => !c)}
          >
            {loading ? 'Verificando...' : 'Verificar'}
          </button>
          <p className={styles.forgotPin}>
            ¿No llegó el código?{' '}
            <button
              type="button"
              className={localStyles.linkBtn}
              onClick={handleResend}
              disabled={loading}
            >
              Solicitar de nuevo
            </button>
          </p>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={handleResetPin}>
          <h2 className={styles.title}>Nuevo PIN</h2>
          <p className={styles.subtitle}>
            Define tu nuevo PIN de {pinLength} dígitos.
          </p>
          <PinLengthToggle
            value={pinLength}
            onChange={handlePinLengthChange}
            disabled={loading}
            label="Longitud del nuevo PIN"
            hint="Elige 4 o 6 dígitos. 6 dígitos es más seguro."
          />
          <label className={localStyles.pinLabel}>Nuevo PIN</label>
          <div className={styles.pinWrapper}>
            <PinInput
              ref={newPinRef}
              length={pinLength}
              value={newPin}
              onChange={setNewPin}
              disabled={loading}
              ariaLabel="Nuevo PIN"
            />
          </div>
          <label className={localStyles.pinLabel}>Confirmar PIN</label>
          <div className={styles.pinWrapper}>
            <PinInput
              length={pinLength}
              value={confirmPin}
              onChange={setConfirmPin}
              disabled={loading}
              ariaLabel="Confirmar PIN"
            />
          </div>
          {error && <p className={styles.error}>{error}</p>}
          {success && <p className={localStyles.success}>{success}</p>}
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={
              loading ||
              newPin.some((d) => !d) ||
              confirmPin.some((d) => !d)
            }
          >
            {loading ? 'Guardando...' : 'Enviar'}
          </button>
        </form>
      )}
    </div>
  );
}
